import { useStandardProductContext } from "app/providers/StandardProductProvider";
import { useMemo } from "react";
import { ProductCard } from "./ProductCard";
import { CategoryStandardProduct } from "shared/api/middleware-api-client";
import { useStandardProductCategories } from "shared/api/hooks/useStandardProductCategories";
import { ProductCardSkeleton } from "./ProductCardSkeleton";
import { useRecommendedSamples } from "widgets/samples/helpers/useRecommendedSamples";

const sampleSize = require("lodash.samplesize");

const recommendedProductCodes = [
  "eindprofiel",
  "kleurstaal",
  "zoschoonglasstart",
  "zoschoonglasnavul",
  "zoschoonglasdoek",
  "zoschoonglasdoekxl",
  "zoschoonallesstart",
  "zoschoonallesnavul",
  "zoschoonallesdoek",
];
export type ProductDetails = {
  title: string;
  description: string;
  price: string;
  imageUrl: string;
  additionalDesc?: boolean;
  product: CategoryStandardProduct;
};
export const StandardProductCardList = () => {
  const { store: standardProductStore } = useStandardProductContext();
  const { standardProductCategories, categoriesLoading } =
    useStandardProductCategories();
  const { getRecommendedSamples } = useRecommendedSamples();

  const selectedProduct = standardProductStore.selectedStandardProduct;
  const productCode = selectedProduct?.code ?? "";
  const eindprofielList = useMemo(() => {
    return standardProductCategories?.find(
      (category) => category.code === "accessoireakoestischpaneel",
    )?.product;
  }, [standardProductCategories]);

  const eindprofielSelection = useMemo(() => {
    if (!eindprofielList || !productCode) return null;

    const eindprofielral9005 = eindprofielList.find((product) =>
      product.standardProduct.code.includes("eindprofielral9005"),
    )?.standardProduct;
    const beigeEindProfiel = eindprofielList.find((product) =>
      product.standardProduct.code.includes("beige"),
    )?.standardProduct;

    if (productCode.includes("zwartvilt")) {
      return eindprofielral9005;
    } else if (productCode.includes("beigevilt")) {
      return beigeEindProfiel;
    }
    return null;
  }, [eindprofielList, productCode]);

  const productList = useMemo(() => {
    if (recommendedProductCodes.some((code) => productCode.includes(code))) {
      const recommendedCodes = getRecommendedSamples(productCode);

      // Category
      const filteredData =
        standardProductStore.standardProductCategories.filter(
          (item) =>
            item.code.includes("kleurstaal") ||
            item.code.includes("glasreiniger") ||
            item.code.includes("Zo"),
        );

      const recommendedProducts = filteredData
        .flatMap((category) => category.product) // Flatten the products from all categories
        .filter((product) =>
          recommendedCodes.includes(product.standardProduct.code),
        ) // Filter products by the recommended IDs
        .map((product) => product.standardProduct); // Return only the standardProduct

      return recommendedProducts;
    }
    // Filter categories by the 'code' values 'gereedschap' or 'montageservice'
    const selectedCategories =
      standardProductStore.standardProductCategories.filter(
        (category) => category.code === "gereedschap",
      );

    // Only return the "standardproduct"
    const selectedProducts = selectedCategories.flatMap(
      (category) =>
        category.code === "montageservice"
          ? [category.product[0].standardProduct] // Only the first product for 'montageservice'
          : category.product.map((productItem) => productItem.standardProduct), // All products for 'gereedschap'
    );

    const endProducts = sampleSize(
      selectedProducts,
      eindprofielSelection ? 2 : 3,
    );

    return eindprofielSelection
      ? [...endProducts, eindprofielSelection]
      : endProducts;
  }, [standardProductStore.standardProductCategories, eindprofielSelection]);

  if (categoriesLoading) return <ProductCardSkeleton />;
  if (productList.length === 0) return null;

  return (
    <div className="gg-flex gg-flex-col gg-gap-5">
      {productList.map((product: CategoryStandardProduct) => (
        <div
          key={product.id}
          className="gg-relative gg-w-full gg-rounded gg-bg-white gg-shadow-lg"
        >
          <ProductCard product={product} />
        </div>
      ))}
    </div>
  );
};
